// An attack from the dice bag (issue 1124; docs/VTT/design/29-the-attack-chain.md §2.1, §4, §6): the form that asks
// for one — with which token, at which target, which attack (a monster's read from its stat block, or typed) — the
// DM's prompt for a defence the table lacks, and a player's line. The browser names the attack and the two tokens and
// nothing else: the server reads the defence, throws the dice and works the outcome out. The ability book (#1127) and
// the action bar (#1130) will fire the same request.
import { AdvantageControl } from './AdvantageControl';
import type { AdvantageAsk } from '@dragons-table/vtt-shared';
import { useEffect, useState } from 'react';
import {
  damageDealt, stopLineText, ATTACK_LIMITS, CHANGED_RESULTS, withAddedSwings, type AttackOption, type AddedSwings, attackOutcome, iterativesOf, sheetFullSwings, statFullSwings, FULL_SWINGS_MAX, tokenLabel, damageOf, damageText, parseDamageText, playerAttackLines, specOfStatAttack, viewDefaults,
  type AttackDefaults, type AttackSpec, type AttackTerm, type DamageSpec, type RulesView, type Band, type Concealment, type Defence, type RollRecord, type PlayerAttack, type StatAttack,
} from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchActions, statBlock } from '../../entries';
import { ChosenRulings } from './Rulings';
import { useTableState } from '../../table/TableState';
import { useNumberWords } from '../../table/numberWords';
import { useShell } from '../context';
import { MeleeAsk, StagedAsk } from './StagedParts';
import { BTN } from '../ui';
import { PlayerFullSwing, rememberFullAttack, waitsOnSwing } from './FullAttackParts';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike {
  id: string; kind: string; name?: string; mine?: boolean; character?: string | null;
  entry?: { segment: string; slug: string; id?: string } | { name: string };
  values?: { sheet?: Array<{ purpose: string; words: string[]; value: number; lines?: Array<{ label: string; value: number }> }> };
}

/** Where an attack's numbers came from (qa-runs#368): its token's sheet or stat block, or typed. */
type Source = 'sheet' | 'stat block';
/** A typed attack, or one of the attacker's own: a stat-block attack at one of its iterative bonuses, a sheet's weapon, a combat maneuver. */
type Choice = { key: string; label: string; spec: AttackSpec; from?: Source; /** Its source names no critical: the system's. */ systemCritical?: boolean; /** One iterative of a stat-block attack: its extra attack is made at the first. */ iterative?: { attack: number; at: number }; /** The stat-block attack it is one bonus of (a full attack's swings, qa-runs#1158). */ stat?: StatAttack };

const TYPED = 'typed';
/** Said in the form of a token with nothing to read its attacks from (qa-runs#368). */
export const NO_SOURCE = "This token isn't linked to a creature or character. Link it in Properties.";
const clampInt = (v: string, min: number, max: number, dflt: number): number => {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) && v.trim() !== '' ? Math.max(min, Math.min(max, n)) : dflt;
};
const signed = (n: number) => `${n >= 0 ? '+' : ''}${n}`;
const sumOf = (terms: AttackTerm[]) => terms.reduce((a, t) => a + t.value, 0);

/** The choices a stat block's attacks give: each attack at its first bonus, then its iteratives, at the system's defaults where it says nothing. */
export function choicesOf(attacks: StatAttack[], defaults: AttackDefaults): Choice[] {
  return attacks.flatMap((a, i) => a.bonuses.map((b, k) => ({
    key: `${i}-${k}`,
    label: `${a.name} ${b >= 0 ? '+' : ''}${b}${k > 0 ? ` (attack ${k + 1})` : ''}${a.ranged ? ' ranged' : ''}${a.touch ? ' touch' : ''}`,
    spec: specOfStatAttack(a, defaults, k), from: 'stat block' as const, systemCritical: !!defaults.critical && a.threat === undefined, iterative: { attack: i, at: k }, stat: a,
  })));
}

/**
 * A combat maneuver (qa-runs#368: Pathfinder's CMB against CMD), for a system whose defences name a CMD: from the
 * sheet's combat maneuver bonus (its lines when they add up) or the stat block's CMB. Null without one.
 */
export function maneuverOf(from: Source, cmb: { value: number; lines?: Array<{ label: string; value: number }> } | null, defaults: AttackDefaults, defences: Array<{ key: string }>): Choice | null {
  if (!cmb || !defences.some((d) => d.key === 'cmd')) return null;
  const lines = cmb.lines && cmb.lines.length > 1 && sumOf(cmb.lines) === cmb.value ? cmb.lines.map((l) => ({ label: l.label, value: l.value })) : [{ label: 'CMB', value: cmb.value }];
  return {
    key: 'cmb', label: `Combat maneuver ${signed(cmb.value)} against CMD`, from, systemCritical: !!defaults.critical,
    spec: { name: 'Combat maneuver', terms: lines, ...(defaults.critical ? { ...defaults.critical } : {}), defence: 'cmd' },
  };
}

/** The stat block's CMB ("+10", "+10 (+14 grapple)"): its first number, or null. */
const statCmb = (stats: Array<{ key?: string; label: string; value: string }>): { value: number } | null => {
  const s = stats.find((x) => x.key === 'cmb' || x.label.trim().toUpperCase() === 'CMB');
  const m = s?.value.match(/[+-]?\d+/);
  return m ? { value: Number(m[0]) } : null;
};

/** A stat block's full attack option in words (qa-runs#1158): "2 claws +5, bite +0", "greatclub +16/+11". */
export const fullOptionLabel = (o: StatAttack[]): string => o.map((a) => `${a.count > 1 ? `${a.count} ` : ''}${a.name} ${a.bonuses.map(signed).join('/')}`).join(', ');

/**
 * A character's base attack bonus (qa-runs#1158), for its iteratives: the sheet's `base_attack` number the token carries,
 * else the weapon's own BAB line; 0 (one swing) when neither says.
 */
export function babOf(sheet: Array<{ purpose: string; value: number }> | undefined, terms: AttackTerm[]): number {
  const s = sheet?.find((n) => n.purpose === 'base_attack');
  if (s) return s.value;
  return terms.find((t) => /^(bab|base attack)/i.test(t.label.trim()))?.value ?? 0;
}

/**
 * A full attack's swings (qa-runs#1158; design/29 §2.9): a character's chosen weapon at each iterative of its base attack
 * bonus then its off-hand weapon's one swing; a creature's chosen full attack option, every attack times its count at
 * every bonus (or, from a stat block that names no full attack, the chosen attack at each of its bonuses). An effect's
 * extra attack (Rapid Shot) is the form's to add, at the first bonus, second in order. Null: nothing to make a full attack of.
 */
export function fullSwingsFor(o: {
  chosen: Choice | undefined; main: AttackSpec; others: Choice[]; bab: number; options: StatAttack[][]; pick: number; defaults: AttackDefaults;
  concealment: Concealment | null; improvedCover?: boolean;
}): { swings: AttackSpec[]; extraAt?: number } | null {
  const { chosen } = o;
  if (!chosen?.from || chosen.key === 'cmb') return null;
  const hide = (x: AttackSpec): AttackSpec => ({ ...x, ...(o.concealment ? { concealment: o.concealment } : {}), ...(o.improvedCover ? { improvedCover: true as const } : {}) });
  let base: AttackSpec[];
  if (chosen.from === 'sheet') {
    const weapons = o.others.filter((c) => c.from === 'sheet' && c.key !== chosen.key && c.key !== 'cmb');
    // The chosen weapon as changed for this roll; ticked Off hand, it is the off-hand swing after the first other weapon's.
    const offChosen = !!chosen.spec.offHand;
    const main = offChosen ? weapons.find((c) => !c.spec.offHand)?.spec ?? null : o.main;
    const off = offChosen ? o.main : weapons.find((c) => c.spec.offHand)?.spec ?? null;
    base = main ? sheetFullSwings(hide(main), o.bab, off ? hide(off) : null) : [o.main];
  } else {
    const option = o.options.length ? o.options[Math.min(o.pick, o.options.length - 1)]! : chosen.stat ? [chosen.stat] : null;
    if (!option) return null;
    base = statFullSwings(option, o.defaults).map(hide);
  }
  return base.length ? { swings: base } : null;
}

const signedWords = (n: number) => (n > 0 ? `+${n}` : `\u2212${Math.abs(n)}`);

/** Where a number came from, in words (qa-runs#368): "+7 from the sheet: BAB 5, Str 2", "+8 from the stat block". */
export function bonusSource(terms: AttackTerm[], from: Source): string {
  const total = sumOf(terms);
  const lines = terms.length > 1 ? `: ${terms.map((t) => `${t.label} ${t.value}`).join(', ')}` : '';
  return `${signed(total)} from the ${from}${lines}`;
}

/**
 * A token's own attacks, from its source (qa-runs#368): a token standing for a character, its sheet's weapons (the
 * server reads them with this person's bearer: the owner's and the DM's); a token linked to a creature, the DM's
 * stat-block attacks (a player is never sent a monster's numbers); on a system with a CMD, a combat maneuver. Shared
 * by the attack form and the dice bag's Attacks section, so a pick there is the same choice here.
 */
export function useOwnAttacks(tokenId: string, isDm: boolean): { own: Choice[]; asking: boolean; /** The stat block's full attack options (qa-runs#1158). */ fullOptions: StatAttack[][] } {
  const { store, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.attack;
  const defaults = viewDefaults(systemRules);
  const [own, setOwn] = useState<Choice[]>([]);
  const [fullOptions, setFullOptions] = useState<StatAttack[][]>([]);
  const [asking, setAsking] = useState(false);
  const me = (store.state.objects as Record<string, TokenLike>)[tokenId];
  const ref = me?.kind === 'token' ? me.entry : undefined;
  const linkedCreature = !!ref && 'segment' in ref;
  const sheetCmb = me?.values?.sheet?.find((n) => n.words.some((w) => w.toLowerCase() === 'cmb')) ?? null;
  const sourceKey = `${tokenId} ${me?.character ?? ''} ${linkedCreature ? `${ref.segment}/${ref.slug}/${ref.id ?? ''}` : ''} ${sheetCmb ? `${sheetCmb.value}` : ''} ${view && defaults ? 'rules' : ''}`;
  useEffect(() => {
    setOwn([]);
    setFullOptions([]);
    let live = true;
    const offer = (got: Choice[]) => { if (!live) return; setAsking(false); setOwn(got); };
    if (!view || !defaults || !me || me.kind !== 'token') { setAsking(false); return () => { live = false; }; }
    if (me.character) {
      // A token standing for a character: its sheet's weapons, as the server reads them for this person.
      setAsking(true);
      void fetchActions(campaignId, tokenId, scene?.id ?? null).then((a) => {
        const sheet = a?.from === 'sheet' ? a.actions.map((x): Choice => ({ key: x.key, label: x.label, spec: x.spec, from: 'sheet', systemCritical: !!x.systemCritical })) : [];
        const cmb = maneuverOf('sheet', sheetCmb, defaults, view.defences);
        offer(cmb ? [...sheet, cmb] : sheet);
      });
    } else if (isDm && linkedCreature) {
      // A creature's attacks, read from its stat block (the DM's alone): the attacker's copy from when it was matched (issue 1612, decision A).
      setAsking(true);
      void statBlock(campaignId, ref, undefined, { object: tokenId, scene: scene?.id ?? null }).then((b) => {
        if (!b || b === 'gone') { offer([]); return; }
        const cmb = maneuverOf('stat block', statCmb(b.stats), defaults, view.defences);
        const block = choicesOf(b.attacks ?? [], defaults);
        if (live) setFullOptions(b.full ?? []);
        offer(cmb ? [...block, cmb] : block);
      });
    } else setAsking(false);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, isDm, campaignId, scene?.id]);
  return { own, asking, fullOptions };
}

/**
 * The attack form. A player attacks with a token of theirs, the DM with any; the target is any other token this person
 * can see. The attacker's own attacks are offered from its source (qa-runs#368): a token standing for a character, its
 * sheet's weapons (the server reads them with this person's bearer: the owner's and the DM's); a token linked to a
 * creature, the DM's stat-block attacks (a player is never sent a monster's numbers). Each fills the form's numbers,
 * which say where they came from and stay editable for the one roll; only a token with no source opens empty, saying
 * why. The DM's attack follows the bag's Private toggle and fudge band, as any throw of theirs does (decision 1, issue
 * 1038). The defences, the miss chances, the critical and the dice are the table's system's (issue 1303); a system
 * the table cannot attack in says why.
 */
export function AttackForm({ isDm, priv, band, onClose, attacker, initialChoice }: { isDm: boolean; priv: boolean; band: Band | null; onClose: () => void; /** The token to attack with (the dice bag's Attacks section); else the selected one of this person's. */ attacker?: string; /** The attack the Attacks section picked, by its key. */ initialChoice?: string }) {
  const { systemRules } = useTableState();
  const view = systemRules.attack;
  const defaults = viewDefaults(systemRules);
  if (!view || !defaults) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="attack-form">
        {systemRules.attackRefusal ?? "This table's game system has no attack rules, so the table cannot resolve an attack."}
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  return <SystemAttackForm isDm={isDm} priv={priv} band={band} onClose={onClose} view={view} defaults={defaults} {...(attacker ? { attackerId: attacker } : {})} {...(initialChoice ? { initialChoice } : {})} />;
}

/** Who may set the table's reasons aside (§5.1): the DM. A player's Normal only takes their own word back. */
const askFrom = (ask: AdvantageAsk, isDm: boolean, known: number): AdvantageAsk | null => (ask === 'normal' ? (isDm && known > 0 ? 'normal' : null) : ask);

function SystemAttackForm({ isDm, priv, band, onClose, view, defaults, attackerId, initialChoice }: { isDm: boolean; priv: boolean; band: Band | null; onClose: () => void; view: NonNullable<RulesView['attack']>; defaults: AttackDefaults; attackerId?: string; initialChoice?: string }) {
  const { store, act, setNotice, rules, rulings, scene } = useTableState();
  const words = useNumberWords();
  const crit = view.critical;
  const top = view.dice.count * view.dice.sides;
  const { campaignId } = useShell();
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => isDm || o.mine);
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [attacker, setAttacker] = useState((attackerId && mine.some((o) => o.id === attackerId) ? attackerId : null) ?? picked ?? mine[0]?.id ?? '');
  const targets = tokens.filter((o) => o.id !== attacker);
  const [target, setTarget] = useState('');
  const [choice, setChoice] = useState(TYPED);
  const [name, setName] = useState(''); // empty, with "Attack" as its placeholder and its name when left blank (issue 1469)
  const [bonus, setBonus] = useState('0');
  const [threat, setThreat] = useState(crit ? String(crit.threat) : '');
  const [mult, setMult] = useState(crit ? String(crit.multiplier) : '');
  const [defence, setDefence] = useState<Defence>(defaults.main);
  const [damage, setDamage] = useState('');
  const [ranged, setRanged] = useState(false);
  const [concealment, setConcealment] = useState('');
  const [improvedCover, setImprovedCover] = useState(false);
  const [advantage, setAdvantage] = useState<AdvantageAsk | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  // The stance chosen with this attack (`features.combat.stances`): it lasts until the attacker's next turn.
  const [stance, setStance] = useState('');
  // The DM's rulings ticked for this attack (issue 1136): their ids travel on the request.
  const [ruled, setRuled] = useState<string[]>([]);

  // The attacker's own attacks, from its source (qa-runs#368).
  const me = tokens.find((o) => o.id === attacker);
  const ref = me?.entry;
  const { own, asking, fullOptions } = useOwnAttacks(attacker, isDm);
  const [fullPick, setFullPick] = useState(0);
  const [withExtra, setWithExtra] = useState(true);
  // What the attacker's entries give its attacks (qa-runs#1159), as the server reads them: the choices offered here
  // (Manyshot, Whirlwind Attack, flurry of blows) and the swings its full attack gains (shown in the summary).
  const [offer, setOffer] = useState<{ options: AttackOption[]; adds: AddedSwings[] }>({ options: [], adds: [] });
  const [withOption, setWithOption] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setOffer({ options: [], adds: [] });
    if (!attacker) return;
    act({ kind: 'attack.options', attacker } as never, (reply) => { if (reply.ok) setOffer({ options: reply.options ?? [], adds: reply.adds ?? [] }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attacker]);
  // The first of them chosen as they arrive, or the one the dice bag's Attacks section picked (slice B).
  useEffect(() => {
    setChoice(initialChoice && own.some((c) => c.key === initialChoice) ? initialChoice : own[0]?.key ?? TYPED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [own]);
  const choices = own;
  const chosen = choices.find((c) => c.key === choice);
  // A chosen attack fills the numbers; each stays editable for this roll. Typed starts empty.
  useEffect(() => {
    const s = chosen?.spec;
    setName(s ? s.name : '');
    setBonus(String(s ? sumOf(s.terms) : 0));
    setThreat(crit ? String(s?.threat ?? crit.threat) : '');
    setMult(crit ? String(s?.multiplier ?? crit.multiplier) : '');
    setDefence(s?.defence ?? defaults.main);
    setDamage(s?.damage ? damageText(s.damage) : '');
    setRanged(!!s?.ranged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen]);
  // As the turn order names it (qa-runs 149): six "Goblin" tokens are "Goblin 1" to "Goblin 6" here too.
  const nameOf = (o: TokenLike | undefined) => (o && tokenLabel(o, store.state.encounter)) || 'Unnamed token';

  // Typed damage reads like the stat block's ("2d6 + 3", "1d8 plus 1d6 fire"): any die from d2 to d100 (issue 1132).
  const typedDamage = damage.trim() ? parseDamageText(damage) : null;
  const badDamage = damage.trim() !== '' && !typedDamage;
  const bonusNow = clampInt(bonus, -ATTACK_LIMITS.termValue, ATTACK_LIMITS.termValue, 0);
  const sameDamage = (d: DamageSpec | undefined) => damage.trim() === (d ? damageText(d) : '');
  const spec = (key = choice): AttackSpec => {
    const own = choices.find((c) => c.key === key);
    let base: AttackSpec;
    if (own && key !== choice) base = own.spec; // another iterative's, as its source gives it
    else if (own) {
      // The chosen attack as its source gives it, with what was changed for this roll.
      const s = own.spec;
      const total = sumOf(s.terms);
      const { damage: _d, ranged: _r, threat: _t, multiplier: _m, ...rest } = s;
      const dmg = sameDamage(s.damage) ? s.damage : typedDamage;
      base = {
        ...rest, name: name.trim().slice(0, ATTACK_LIMITS.name) || s.name,
        terms: bonusNow === total ? s.terms : [...s.terms, { label: 'changed for this roll', value: bonusNow - total }],
        ...(crit ? { threat: clampInt(threat, view.dice.count, top, s.threat ?? crit.threat), multiplier: clampInt(mult, 2, ATTACK_LIMITS.multiplier, s.multiplier ?? crit.multiplier) } : {}), defence,
        ...(dmg ? { damage: dmg } : {}), ...(ranged ? { ranged: true } : {}),
      };
    } else {
      base = {
        name: name.trim().slice(0, ATTACK_LIMITS.name) || 'Attack',
        terms: [{ label: 'attack', value: bonusNow }],
        ...(crit ? { threat: clampInt(threat, view.dice.count, top, crit.threat), multiplier: clampInt(mult, 2, ATTACK_LIMITS.multiplier, crit.multiplier) } : {}), defence,
        ...(typedDamage ? { damage: typedDamage } : {}), ...(ranged ? { ranged: true } : {}),
      };
    }
    // The target's concealment is the moment's (design/29 §2.6): the attacker or the DM says so as they attack.
    const covered = improvedCover && view.improvedCover ? { ...base, improvedCover: true as const } : base;
    return concealment ? { ...covered, concealment: Number(concealment) as Concealment } : covered;
  };
  const ready = attacker !== '' && target !== '' && target !== attacker && !badDamage;
  // An extra attack an effect on the attacker's combatant gives (issue 1509; docs/MRCS/feats.md §10 step 6: Rapid
  // Shot's), as this person was sent it: at the chosen attack's first bonus, of the effect's reach. The server reads the
  // effect again and refuses one that is gone or used.
  const enc = store.state.encounter as { combatants?: Array<{ tokens: string[]; effects?: Array<{ label: string; extra?: { left: number; only?: 'melee' | 'ranged' } }> }> } | undefined;
  const giving = (enc?.combatants ?? []).filter((c) => c.tokens.includes(attacker)).flatMap((c) => c.effects ?? []).find((e) => e.extra);
  const extraSpec = (): AttackSpec => spec(chosen?.iterative ? `${chosen.iterative.attack}-0` : choice);
  const reachFits = !giving?.extra?.only || (giving.extra.only === 'ranged') === !!extraSpec().ranged;
  // Only rulings still in force: one the DM removed since it was ticked is no longer offered, and not sent.
  // The situations this attack can be in, and what the ticked ones say about advantage.
  const fitting = (view.situations ?? []).filter((s) => !s.only || (s.only === 'ranged') === (spec().ranged === true));
  const tickedNow = fitting.filter((s) => ticked.includes(s.key));
  const known = tickedNow.flatMap((s) => (s.with ? [{ with: s.with, name: s.label }] : []));
  const asked = () => { const live = ruled.filter((id) => (rulings ?? []).some((r) => r.id === id)); return { ...(advantage ? { advantage } : {}), ...(tickedNow.length ? { situations: tickedNow.map((s) => s.key) } : {}), private: isDm ? priv : false, ...(isDm && priv && band ? { band } : {}), ...(live.length ? { rulings: live } : {}), ...(stance ? { stance } : {}) }; };
  const send = (extra: boolean) => {
    if (!ready) return;
    const body = { kind: 'attack.request', attacker, target, attack: extra ? extraSpec() : spec(), ...asked(), ...(extra ? { extra: true } : {}) };
    act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That attack could not be made.'); });
  };
  // A full attack (qa-runs#1158): every swing from the chosen source, in order, spending the turn's full-round action once.
  const fullBase = chosen ? fullSwingsFor({ chosen, main: spec(), others: choices, bab: babOf(me?.values?.sheet, chosen.spec.terms), options: chosen.from === 'stat block' ? fullOptions : [], pick: fullPick, defaults, improvedCover: improvedCover && !!view.improvedCover, concealment: concealment ? Number(concealment) as Concealment : null }) : null;
  const extraFits = !!giving?.extra && !!fullBase && (!giving.extra.only || (giving.extra.only === 'ranged') === !!fullBase.swings[0]!.ranged);
  const full = fullBase && extraFits && withExtra ? { swings: [fullBase.swings[0]!, ...fullBase.swings].slice(0, FULL_SWINGS_MAX), extraAt: 1 } : fullBase;
  // The options this attack may take (qa-runs#1159): of its reach, and not barred by an effect on the attacker (Rapid Shot).
  const effects = (enc?.combatants ?? []).filter((c) => c.tokens.includes(attacker)).flatMap((c) => c.effects ?? []) as Array<{ label: string; rule?: { entry?: string } }>;
  const usable = offer.options.filter((o) => (!o.ranged || ranged) && !(o.notWith && effects.some((e) => e.rule?.entry === o.notWith)));
  const fullOpts = usable.filter((o) => o.action === 'full' && o.shape !== 'each_foe');
  const chosenFull = fullOpts.find((o) => withOption[o.id]);
  const offerFull = !!full && (full.swings.length >= 2 || !!chosenFull || offer.adds.length > 0);
  // The swings the server adds as it makes the full attack (two-weapon feats, haste, a speed weapon), listed here too.
  const shown = full ? withAddedSwings(full.swings, offer.adds, () => true).swings : [];
  const sendOption = (o: AttackOption) => {
    if (!ready) return;
    const body = { kind: 'attack.request', attacker, target, attack: spec(), option: o.id, ...asked() };
    act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? `${o.name} could not be made.`); else if (reply.notice) setNotice(reply.notice); });
  };
  const sendFull = () => {
    if (!ready || !full) return;
    rememberFullAttack(campaignId, attacker, full.swings);
    const body = { kind: 'attack.request', attacker, target, attack: full.swings[0], full: full.swings, ...(full.extraAt !== undefined ? { extraAt: full.extraAt } : {}), ...(chosenFull ? { option: chosenFull.id } : {}), ...asked() };
    act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That full attack could not be made.'); else if (reply.notice) setNotice(reply.notice); });
  };

  if (mine.length === 0) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="attack-form">
        {isDm ? 'Put a token on the board to attack with it.' : 'You have no token to attack with.'}
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  // Where each number came from (qa-runs#368), and a number changed for this roll said as such.
  const from = chosen?.from;
  const s0 = chosen?.spec;
  const sources: string[] = [];
  if (from && s0) {
    const total = sumOf(s0.terms);
    sources.push(bonusNow === total ? `bonus ${bonusSource(s0.terms, from)}` : `bonus ${signed(bonusNow)} for this roll (the ${from} says ${signed(total)})`);
    if (crit) {
      const said = `${s0.threat ?? crit.threat}${(s0.threat ?? crit.threat) < top ? `–${top}` : ''} ×${s0.multiplier ?? crit.multiplier}`;
      const changed = String(s0.threat ?? crit.threat) !== threat.trim() || String(s0.multiplier ?? crit.multiplier) !== mult.trim();
      sources.push(changed ? `critical changed for this roll (${chosen.systemCritical ? "the system's" : `the ${from}'s`} is ${said})` : `critical ${said} ${chosen.systemCritical ? `the system's: the ${from} names none` : `from the ${from}`}`);
    }
    if (s0.damage) sources.push(sameDamage(s0.damage) ? `damage ${damageText(s0.damage)} from the ${from}` : `damage changed for this roll (the ${from} says ${damageText(s0.damage)})`);
    sources.push(`${s0.ranged ? 'ranged' : 'melee'}${!!s0.ranged === ranged ? ` from the ${from}` : ` by the ${from}; ${ranged ? 'ranged' : 'melee'} for this roll`}`);
  }
  const unlinked = !me?.character && !(ref && ('segment' in ref || 'name' in ref));
  return (
    <form className="space-y-1 rounded border border-amber-700 p-2 text-xs" data-testid="attack-form" onSubmit={(e) => {
      e.preventDefault();
      send(false);
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">With
          <select aria-label="Attack with" className={field} value={attacker} onChange={(e) => { setAttacker(e.target.value); if (e.target.value === target) setTarget(''); }}>
            {mine.map((o) => <option key={o.id} value={o.id}>{nameOf(o)}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">at
          <select aria-label="Target" className={field} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose a target</option>
            {targets.map((o) => <option key={o.id} value={o.id}>{nameOf(o)}</option>)}
          </select>
        </label>
      </div>
      {unlinked && <p className="text-amber-300" data-testid="attack-no-source">{NO_SOURCE}</p>}
      {!unlinked && !asking && choices.length === 0 && (
        <p className="text-stone-400" data-testid="attack-no-attacks">{me?.character ? "Its character's sheet gives no attack the table can read: type it here." : isDm ? "Its stat block gives no attack the table can read: type it here." : "This creature's numbers are the DM's: type the attack here."}</p>
      )}
      {choices.length > 0 && (
        <label className="flex items-center gap-1">Attack
          <select aria-label="Which attack" className={field} value={choice} onChange={(e) => setChoice(e.target.value)}>
            {choices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            <option value={TYPED}>Another attack…</option>
          </select>
        </label>
      )}
      <div className="flex flex-wrap items-center gap-1" data-testid={choice === TYPED ? 'attack-typed' : 'attack-filled'}>
        <input aria-label="Attack name" placeholder="Attack" maxLength={ATTACK_LIMITS.name} className={`${field} w-28`} value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-1">bonus <input aria-label="Attack bonus" type="number" className={`${field} w-12`} value={bonus} onChange={(e) => setBonus(e.target.value)} /></label>
        {crit && <label className="flex items-center gap-1" title="The lowest natural roll that threatens a critical hit.">threat <input aria-label="Threat range from" type="number" min={view.dice.count} max={top} className={`${field} w-12`} value={threat} onChange={(e) => setThreat(e.target.value)} />–{top}</label>}
        {crit && <label className="flex items-center gap-1">×<input aria-label="Critical multiplier" type="number" min={2} max={ATTACK_LIMITS.multiplier} className={`${field} w-10`} value={mult} onChange={(e) => setMult(e.target.value)} /></label>}
        <select aria-label="Melee or ranged" className={field} value={ranged ? 'ranged' : 'melee'} onChange={(e) => setRanged(e.target.value === 'ranged')} title="A prone target is easier to hit in melee and harder at range: conditions read this.">
          <option value="melee">melee</option>
          <option value="ranged">ranged</option>
        </select>
        <select aria-label="Against" className={field} value={defence} onChange={(e) => setDefence(e.target.value as Defence)}>
          {view.defences.map((d) => <option key={d.key} value={d.key}>against {d.label}</option>)}
        </select>
        <label className="flex items-center gap-1">damage <input aria-label="Damage" placeholder="2d6 + 3" maxLength={80} className={`${field} w-28`} value={damage} onChange={(e) => setDamage(e.target.value)} /></label>
        {badDamage && <span className="text-amber-300" data-testid="attack-damage-hint">Damage reads like 2d6 + 3, or 1d8 plus 1d6 fire.</span>}
      </div>
      {sources.length > 0 && (
        <ul className="text-stone-400" data-testid="attack-sources">
          {sources.map((t) => <li key={t} data-testid={t.startsWith('damage') ? 'attack-damage' : undefined}>{t}</li>)}
        </ul>
      )}
      {view.concealment.length > 0 && (
        <label className="flex items-center gap-1" title="Concealment gives a miss chance, rolled on a d100 after a hit (fog, dim light, a blur spell).">
          <select aria-label="Concealment" className={field} value={concealment} onChange={(e) => setConcealment(e.target.value as typeof concealment)}>
            <option value="">No concealment</option>
            {view.concealment.map((c) => <option key={c.missChance} value={String(c.missChance)}>{c.label[0]!.toUpperCase()}{c.label.slice(1)} ({c.missChance}%)</option>)}
          </select>
        </label>
      )}
      {fitting.map((s) => (
        <label key={s.key} className="flex items-center gap-1">
          <input type="checkbox" data-testid={`attack-situation-${s.key}`} checked={ticked.includes(s.key)} onChange={(e) => setTicked(e.target.checked ? [...ticked, s.key] : ticked.filter((k) => k !== s.key))} />
          {s.label}
        </label>
      ))}
      {view.advantage && <AdvantageControl id="attack" label="Advantage on this attack" value={advantage} known={known} onChange={(a) => setAdvantage(askFrom(a, isDm, known.length))} />}
      {view.improvedCover && (
        <label className="flex items-center gap-1" title="The board works cover out from its walls and tokens; it cannot see an arrow slit. Tick this when the target has the doubled cover.">
          <input type="checkbox" data-testid="attack-improved-cover" checked={improvedCover} onChange={(e) => setImprovedCover(e.target.checked)} />
          {view.improvedCover}
        </label>
      )}
      {view.stances?.map((s) => (
        <label key={s.key} className="flex items-center gap-1" title={`${signedWords(s.attack)} on attacks, ${signedWords(s.ac)} to AC, until this creature's next turn.`}>
          <input type="checkbox" data-testid={`attack-stance-${s.key}`} checked={stance === s.key} onChange={(e) => setStance(e.target.checked ? s.key : '')} />
          {s.label}
        </label>
      ))}
      <ChosenRulings to="attack" chosen={ruled} onChange={setRuled} />
      {offerFull && chosen?.from === 'stat block' && fullOptions.length > 1 && (
        <label className="flex min-w-0 flex-wrap items-center gap-1">Full attack with
          <select aria-label="Full attack option" className={`${field} max-w-full`} value={String(Math.min(fullPick, fullOptions.length - 1))} onChange={(e) => setFullPick(Number(e.target.value))}>
            {fullOptions.map((o, i) => <option key={i} value={String(i)}>{fullOptionLabel(o)}</option>)}
          </select>
        </label>
      )}
      {offerFull && (
        <p className="break-words text-stone-400" data-testid="attack-full-summary">
          Full attack: {shown.map((x, i) => `${x.name} ${signed(sumOf(x.terms))}${i === full!.extraAt ? ` (${giving?.label ?? 'extra'})` : ''}${offer.adds.some((a) => a.name === x.terms[x.terms.length - 1]?.label) ? ` (${x.terms[x.terms.length - 1]!.label})` : ''}${x.offHand ? ' (off hand)' : ''}`).join(', ')}{chosenFull ? `; with ${chosenFull.name}` : ''}
        </p>
      )}
      {offerFull && fullOpts.map((o) => (
        <label key={o.id} className="flex items-center gap-1 text-stone-300" data-testid={`attack-option-${o.id}`}>
          <input type="checkbox" aria-label={`With ${o.name}`} checked={!!withOption[o.id]} onChange={(e) => setWithOption({ [o.id]: e.target.checked })} />
          With {o.name}{o.shape === 'arrows' ? ' (the first attack fires more arrows)' : ''}
        </label>
      ))}
      {offerFull && extraFits && (
        <label className="flex items-center gap-1 text-stone-300">
          <input type="checkbox" aria-label={`With ${giving!.label}'s extra attack`} checked={withExtra} onChange={(e) => setWithExtra(e.target.checked)} />
          With {giving!.label}'s extra attack
        </label>
      )}
      <div className="flex flex-wrap gap-1">
        <button type="submit" className={`${btn} border-amber-600`} disabled={!ready}>Attack</button>
        {offerFull && (
          <button type="button" className={`${btn} border-amber-600`} data-testid="attack-full" disabled={!ready} onClick={sendFull}
            title="Every swing at the target, in order, highest bonus first; it takes the turn's full-round action.">Full attack</button>
        )}
        {giving?.extra && (
          <button type="button" className={`${btn} border-amber-600`} data-testid="attack-extra" disabled={!ready || !reachFits} onClick={() => send(true)}
            title={reachFits ? 'One more attack this round, at its first bonus, taking no action of the turn.' : `${giving.label} gives a ${giving.extra.only} attack.`}>
            {giving.label}: extra attack
          </button>
        )}
        {usable.filter((o) => o.action === 'standard' || o.shape === 'each_foe').map((o) => (
          <button key={o.id} type="button" className={`${btn} border-amber-600`} data-testid={`attack-option-${o.id}`} disabled={!ready} onClick={() => sendOption(o)}
            title={o.shape === 'each_foe' ? 'One attack at full bonus at each enemy next to the attacker; it takes the full-round action.' : 'Several arrows on one attack roll at the target; it takes a standard action.'}>
            {o.name}
          </button>
        ))}
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: players see the outcome only.' : 'Public: players see the total too.'}</span>}
      </div>
      {isDm && (
        // The table's setting (issue 1125; decision C on #1059): damage waits for the DM's confirmation unless set to apply at once.
        <label className="flex items-center gap-1 text-stone-300" title={`Off: a hit's damage waits in your log for Apply, Change or Drop. On: it lowers ${words.health} as the hit lands.`}>
          <input type="checkbox" aria-label="Apply damage at once" checked={rules.damage === 'apply'}
            onChange={(e) => act({ kind: 'table.rules', rules: { ...rules, damage: e.target.checked ? 'apply' : 'confirm' } } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be set.'); })} />
          Apply damage at once, for the whole table
        </label>
      )}
    </form>
  );
}

/** The DM's prompt on an attack whose target has no defence the table knows (design/29 §4): the number, once. `defence` is its name, as the attack's rules stamped it. */
export function SupplyDefence({ rollId, defence }: { rollId: string; defence: string }) {
  const { act, setNotice } = useTableState();
  const [value, setValue] = useState('');
  return (
    <form className="mt-1 flex items-center gap-1 font-sans" data-testid={`attack-ac-${rollId}`} onSubmit={(e) => {
      e.preventDefault();
      const n = Math.trunc(Number(value));
      if (value.trim() === '' || !Number.isFinite(n)) return;
      act({ kind: 'attack.ac', rollId, ac: n } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be set.'); });
    }}>
      <span className="text-amber-300">The target's {defence}?</span>
      <input aria-label={`The target's ${defence}`} type="number" className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
      <button type="submit" className={btn}>Set</button>
    </form>
  );
}

/** A player's line for an attack (design/29 §6): what they may know of it, in the roller's colour. */
export function PlayerAttackEntry({ attack, colour }: { attack: PlayerAttack; colour: string }) {
  const [head, ...rest] = playerAttackLines(attack);
  return (
    <li className="vtt-log-card min-w-0 text-xs" style={{ borderLeftColor: colour }} data-testid={`attack-${attack.id}`} data-result={attack.result}>
      <div className="whitespace-pre-wrap break-words">
        <span style={{ color: colour }} className="font-semibold">{head}</span>
        {rest.length > 0 && <span className="text-stone-400">{`\n${rest.join('\n')}`}</span>}
      </div>
      {attack.full && <PlayerFullSwing attack={attack} />}
      {attack.ask && <AskButtons rollId={attack.id} name={attack.ask.name} outcomes={attack.ask.outcomes} />}
      {/* A staged attack's defender (issue 1527): their own token's dodge or resistance. */}
      {attack.staged?.ask && attack.staged.ask.step !== 'melee' && <StagedAsk rollId={attack.id} token={attack.staged.ask.token} step={attack.staged.ask.step} question={attack.staged.ask.question} poolKey={attack.staged.ask.pool} />}
      {/* A melee attack's defender (issue 1528): their own skill, weapon and pool dice. */}
      {attack.staged?.ask?.step === 'melee' && <MeleeAsk rollId={attack.id} token={attack.staged.ask.token} question={attack.staged.ask.question} poolKey={attack.staged.ask.pool} />}
    </li>
  );
}

/** Past this many numbers to choose from, the question is a box. */
const MANY_NUMBERS = 12;

/** An End a house rule asks this person to settle (issue 1499; workbench §5.1): its outcomes as buttons. */
export function AskButtons({ rollId, name, outcomes, question, labels }: { rollId: string; name: string; outcomes: string[]; /** The End's own question (issue 1500). */ question?: string | undefined; /** Each button's words when not its outcome (issue 1508: a creature's name). */ labels?: string[] | undefined }) {
  const { act, setNotice } = useTableState();
  const [sent, setSent] = useState(false);
  const [typed, setTyped] = useState('');
  // A chain's next question comes on the same roll: the last answer's lock must not hold its buttons down.
  const asked = `${question ?? name}|${outcomes.join('|')}`;
  useEffect(() => { setSent(false); setTyped(''); }, [asked]);
  const send = (choice: string) => {
    setSent(true);
    act({ kind: 'attack.answer', rollId, choice } as never, (reply) => { if (!reply.ok) { setSent(false); setNotice(reply.reason ?? 'That could not be answered.'); } });
  };
  // A long run of numbers (Lay on Hands' points) is a box, not a wall of buttons.
  if (outcomes.length > MANY_NUMBERS && outcomes.every((o) => /^\d+$/.test(o))) {
    const least = Math.min(...outcomes.map(Number)); const most = Math.max(...outcomes.map(Number));
    return (
      <form className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`attack-ask-${rollId}`} onSubmit={(e) => { e.preventDefault(); if (outcomes.includes(typed.trim())) send(typed.trim()); }}>
        <span className="text-amber-300">{question ?? `${name}?`}</span>
        <input aria-label={question ?? name} type="number" min={least} max={most} step={1} className="w-16 rounded bg-stone-800 px-1 py-0.5 text-xs" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <span className="text-xs text-stone-400">{least} to {most}</span>
        <button type="submit" className={btn} disabled={sent || !outcomes.includes(typed.trim())}>Answer</button>
      </form>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`attack-ask-${rollId}`}>
      <span className="text-amber-300">{question ?? `${name}?`}</span>
      {outcomes.map((o, i) => (
        <button key={o} type="button" className={btn} disabled={sent} onClick={() => send(o)}>{((w) => `${w[0]!.toUpperCase()}${w.slice(1)}`)(labels?.[i] ?? o)}</button>
      ))}
    </div>
  );
}

/**
 * The DM's parts of an attack a house rule played (issue 1499, part 3): the number for a piece the table did not know
 * (as a missing defence is asked), the buttons of an End the DM settles (the acting player's are theirs alone), and,
 * on a table that lets the DM override a rule's outcome (§5.2), "Change the outcome" until its damage is settled.
 */
export function HouseRuleParts({ roll, meId }: { roll: RollRecord; meId: string }) {
  const { act, setNotice, rules } = useTableState();
  const [value, setValue] = useState('');
  const [changing, setChanging] = useState(false);
  const f = roll.attack;
  if (!f) return null;
  const pending = f.house?.pending?.[0];
  const ask = f.house?.chain?.ask;
  const mineToAnswer = ask && (ask.who === 'dm' || roll.actor.id === meId);
  const outcome = attackOutcome(roll, f);
  const canChange = rules.override === true && !f.settled && outcome.result !== 'unresolved';
  const send = (body: Record<string, unknown>) => act({ kind: 'attack.answer', rollId: roll.id, ...body } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be set.'); });
  return (
    <>
      {pending && (
        <form className="mt-1 flex items-center gap-1 font-sans" data-testid={`attack-value-${roll.id}`} onSubmit={(e) => {
          e.preventDefault();
          const n = Math.trunc(Number(value));
          if (value.trim() === '' || !Number.isFinite(n)) return;
          send({ value: n }); setValue('');
        }}>
          <span className="text-amber-300">{pending.label}: what does it add?</span>
          <input aria-label={`What ${pending.label} adds`} type="number" className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className={btn}>Set</button>
        </form>
      )}
      {ask && mineToAnswer && <AskButtons rollId={roll.id} name={ask.name} outcomes={ask.outcomes} />}
      {canChange && !changing && (
        <div className="mt-1 font-sans"><button type="button" className={btn} data-testid={`attack-change-${roll.id}`} onClick={() => setChanging(true)}>Change the outcome</button></div>
      )}
      {canChange && changing && (
        <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`attack-outcomes-${roll.id}`}>
          {CHANGED_RESULTS.filter((r) => r !== 'critical' || f.rules.critical).map((r) => (
            <button key={r} type="button" className={btn} disabled={r === outcome.result} onClick={() => { setChanging(false); send({ outcome: r }); }}>{f.rules.words[r]}</button>
          ))}
          <button type="button" className={btn} onClick={() => setChanging(false)}>Cancel</button>
        </div>
      )}
    </>
  );
}

/**
 * The DM's proposal for a hit's damage (issue 1125; design/29 §2.8): Apply at the rolled amount, Change it first, or
 * Drop it; once settled, what was done. Nothing changes on the board until the DM chooses.
 */
export function DamageProposal({ roll }: { roll: RollRecord }) {
  const { act, setNotice, log } = useTableState();
  const [changing, setChanging] = useState(false);
  const [value, setValue] = useState('');
  const facts = roll.attack;
  const damage = facts ? damageDealt(facts, attackOutcome(roll, facts)) : null;
  if (!facts || !damage || facts.settled) return null;
  const kind = damage.damage.kind;
  // A full attack's swings settle in order (qa-runs#1158): a later hit waits until the earlier ones are applied or dropped.
  const waits = waitsOnSwing(roll, log);
  if (waits !== null) return <div className="mt-1 font-sans text-stone-400" data-testid={`damage-${roll.id}`} data-waiting="yes">{damage.total} damage to {facts.target.name}: waits for swing {waits}'s damage first.</div>;
  const send = (amount: number | null) => act({ kind: 'attack.apply', rollId: roll.id, amount } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be applied.'); });
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`damage-${roll.id}`}>
      <span className="text-amber-300">{damage.total}{kind ? ` ${kind}` : ''} damage to {facts.target.name}?</span>
      {damage.lines.length > 0 && <span className="text-stone-300" data-testid={`damage-stops-${roll.id}`}>({damage.lines.map(stopLineText).join('; ')})</span>}
      <button type="button" className={btn} onClick={() => send(damage.total)}>Apply {damage.total}</button>
      {!changing && <button type="button" className={btn} onClick={() => { setValue(String(damage.total)); setChanging(true); }}>Change…</button>}
      {changing && (
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = Math.trunc(Number(value)); if (value.trim() !== '' && Number.isFinite(n) && n >= 0) send(n); }}>
          <input aria-label="Damage to apply" type="number" min={0} className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className={btn}>Apply</button>
        </form>
      )}
      <button type="button" className={btn} onClick={() => send(null)}>Drop</button>
    </div>
  );
}

/**
 * What the newest attack made with this token still waits on the DM for (the 2026-10-15 session pass): the target's
 * defence when the table had none, then its damage to Apply, Change or Drop — the log's own prompts, under the token's
 * actions too, so a DM who attacks from the action bar or Properties answers there instead of scrolling the dice bag
 * down to the log. The DM's alone (a player's log has no such record); nothing when the attack is settled or missed.
 */
export function PendingAttack({ attackerId }: { attackerId: string }) {
  const { log, role } = useTableState();
  if (role !== 'dm') return null;
  let roll: RollRecord | null = null;
  for (let i = log.length - 1; i >= 0 && !roll; i -= 1) {
    const e = log[i]!;
    if (e.kind === 'roll' && e.roll.attack?.attacker.id === attackerId) roll = e.roll;
  }
  // A full attack (qa-runs#1158): the swing whose damage is offered now, the earliest still open.
  const group = roll?.attack?.full?.group;
  if (group) {
    const open = log.find((e) => e.kind === 'roll' && e.roll.attack?.full?.group === group && !e.roll.attack.settled && (e.roll.attack.ac === null || damageOf(e.roll.attack, attackOutcome(e.roll, e.roll.attack))));
    if (open?.kind === 'roll') roll = open.roll;
  }
  const facts = roll?.attack;
  if (!roll || !facts) return null;
  const needsDefence = facts.ac === null;
  const damage = needsDefence ? null : damageOf(facts, attackOutcome(roll, facts));
  if (!needsDefence && (!damage || facts.settled)) return null;
  return (
    <div className="text-xs" data-testid="pending-attack">
      {needsDefence ? <SupplyDefence rollId={roll.id} defence={facts.rules.defence} /> : <DamageProposal roll={roll} />}
    </div>
  );
}
